import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Package, Mic, ShoppingBag, TrendingUp, Smartphone, Share2, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const baseFeatures = [
  { icon: Package, title: "Stocks & Coût de revient", desc: "Enregistrez vos voyages d'achat (transport, hôtel) et calculez le vrai prix de revient automatiquement." },
  { icon: Share2, title: "Vitrine One-Pager", desc: "Une page boutique unique, partageable sur WhatsApp en un clic. Le bouton Acheter suit votre stock réel." },
  { icon: ShoppingBag, title: "Commandes en 2 étapes", desc: "Le client commande, vous validez les frais de livraison, puis envoyez le lien de paiement." },
  { icon: TrendingUp, title: "Marge nette réelle", desc: "Tableau de bord clair : chiffre d'affaires, coût de revient et bénéfice net en temps réel." },
  { icon: Mic, title: "Copilote IA vocal", desc: "Dictez vos ventes et dépenses à voix haute. L'IA enregistre tout pour vous." },
];

const REGION = {
  default: {
    hero: {
      src: "https://static.prod-images.emergentagent.com/jobs/a7ba0871-0fe0-4d76-aa98-64efe9fe0979/images/cb6279fc8de7e502454c41a78ad8dcdb923f94d77094273a8590ec066cf4b242.png",
      alt: "Entrepreneure ivoirienne sur un marché",
    },
    paragraph: "Stocks, vitrine en ligne, commandes, paiement Mobile Money et un copilote IA qui vous écoute. Tout dans une seule app, optimisée pour votre téléphone.",
    payment: { icon: Smartphone, title: "Mobile Money & espèces", desc: "Wave, MTN, Orange Money ou paiement à la livraison — un lien de paiement généré pour chaque commande." },
  },
  CA: {
    hero: {
      src: "https://static.prod-images.emergentagent.com/jobs/a7ba0871-0fe0-4d76-aa98-64efe9fe0979/images/6dbdfa469e95f3e29f37eedeeed39383b77ad770c12db680390b960a68d29211.png",
      alt: "Entrepreneure canadienne dans sa boutique moderne",
    },
    paragraph: "Stocks, vitrine en ligne, commandes, paiement par carte, Interac, PayPal ou en espèces, et un copilote IA qui vous écoute. Tout dans une seule app, optimisée pour votre téléphone.",
    payment: { icon: CreditCard, title: "Paiements flexibles", desc: "Carte bancaire, Interac, PayPal ou espèces — un lien de paiement généré pour chaque commande." },
  },
  FR: {
    hero: {
      src: "https://static.prod-images.emergentagent.com/jobs/a7ba0871-0fe0-4d76-aa98-64efe9fe0979/images/f917ce07b01d4415de970c217d1174af127f3b88f1c3a47d70107df3a701197e.png",
      alt: "Entrepreneure française dans sa boutique parisienne",
    },
    paragraph: "Stocks, vitrine en ligne, commandes, paiement par carte bancaire, PayPal, virement ou en espèces, et un copilote IA qui vous écoute. Tout dans une seule app, optimisée pour votre téléphone.",
    payment: { icon: CreditCard, title: "Paiements flexibles", desc: "Carte bancaire, PayPal, virement ou espèces — un lien de paiement généré pour chaque commande." },
  },
};

// Détecte la région du visiteur via la langue et le fuseau horaire du navigateur (sans saisie).
function detectRegion() {
  try {
    const lang = (navigator.language || "").toLowerCase();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const caTimezones = [
      "America/Toronto", "America/Montreal", "America/Vancouver", "America/Edmonton",
      "America/Winnipeg", "America/Halifax", "America/St_Johns", "America/Regina",
      "America/Moncton", "America/Iqaluit", "America/Whitehorse", "America/Dawson_Creek",
    ];
    if (lang.endsWith("-ca") || caTimezones.includes(tz)) return REGION.CA;
    if (tz.startsWith("Europe/")) return REGION.FR;
    if (!tz.startsWith("Africa/") && ["-fr", "-be", "-ch", "-lu"].some((s) => lang.endsWith(s))) return REGION.FR;
  } catch (e) { /* fallback below */ }
  return REGION.default;
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const region = React.useMemo(detectRegion, []);
  const hero = region.hero;
  const features = React.useMemo(() => [...baseFeatures, region.payment], [region]);

  return (
    <div className="min-h-screen bg-[#F9F6F0] text-[#1A1A1A]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#F9F6F0]/85 backdrop-blur-md border-b border-[#E5E0D8]">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-[#E85D04] flex items-center justify-center text-white font-display font-extrabold">S</span>
            <span className="font-display font-extrabold text-xl tracking-tight">SuccèsNet</span>
          </div>
          <button
            data-testid="landing-login-btn"
            onClick={() => navigate(user ? (user.role === "admin" ? "/admin" : "/tableau-de-bord") : "/connexion")}
            className="h-11 px-5 rounded-2xl bg-[#1B4332] text-white font-semibold text-sm active:scale-95 transition-transform"
          >
            {user ? "Mon espace" : "Se connecter"}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 fabric-grain opacity-60" />
        <div className="max-w-5xl mx-auto px-4 pt-12 pb-10 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#E85D04] mb-4">
              Pour les entrepreneurs
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight max-w-2xl">
              Digitalisez votre commerce. <span className="text-[#E85D04]">Vendez plus,</span> gérez mieux.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#5C574F] max-w-xl leading-relaxed">
              {region.paragraph}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-start-btn"
                onClick={() => navigate("/connexion")}
                className="h-14 px-7 rounded-2xl bg-[#E85D04] text-white font-bold text-base flex items-center justify-center gap-2 hover:bg-[#D05303] hover:-translate-y-0.5 active:scale-95 transition-all shadow-lg shadow-[#E85D04]/20"
              >
                Démarrer gratuitement <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-10 rounded-3xl overflow-hidden border border-[#E5E0D8] shadow-xl"
          >
            <img
              src={hero.src}
              alt={hero.alt}
              data-testid="hero-image"
              className="w-full h-64 sm:h-80 object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-8">Tout ce qu'il faut pour réussir</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white rounded-xl border border-black/5 shadow-sm p-6"
              >
                <span className="h-12 w-12 rounded-xl bg-[#E85D04]/10 flex items-center justify-center mb-4">
                  <Icon className="h-6 w-6 text-[#E85D04]" strokeWidth={2.4} />
                </span>
                <h3 className="font-display text-lg font-semibold mb-1.5">{f.title}</h3>
                <p className="text-sm text-[#5C574F] leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        <div className="bg-[#1B4332] rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 fabric-grain opacity-20" />
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white relative">Prêt à passer au digital ?</h2>
          <p className="text-white/80 mt-3 max-w-md mx-auto relative">Créez votre boutique en moins de 2 minutes. C'est gratuit.</p>
          <button
            data-testid="cta-start-btn"
            onClick={() => navigate("/connexion")}
            className="mt-6 h-14 px-8 rounded-2xl bg-[#FFBA08] text-[#1A1A1A] font-bold text-base active:scale-95 transition-transform relative"
          >
            Créer ma boutique
          </button>
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] py-6 text-center text-sm text-[#9c968a]">
        <p>© 2026 SuccèsNet — Le commerce africain & francophone, digitalisé.</p>
        <p className="mt-1">Une plateforme éditée par <span className="font-semibold text-[#5C574F]">Agileconnexion inc.</span></p>
        <p className="mt-1">
          Site web : <a href="https://www.succesnet.com" target="_blank" rel="noreferrer" className="hover:text-[#E85D04]" data-testid="footer-website-link">www.succesnet.com</a>
          <span className="mx-1.5">·</span>
          Contact : <a href="mailto:support@succesnet.com" className="hover:text-[#E85D04]" data-testid="footer-contact-email">support@succesnet.com</a>
        </p>
        <p className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <Link to="/mentions-legales" className="hover:text-[#E85D04]" data-testid="footer-legal-mentions">Mentions légales</Link>
          <span className="text-[#D8D2C8]">·</span>
          <Link to="/cgv" className="hover:text-[#E85D04]" data-testid="footer-legal-cgv">CGV / CGU</Link>
          <span className="text-[#D8D2C8]">·</span>
          <Link to="/politique-confidentialite" className="hover:text-[#E85D04]" data-testid="footer-legal-confidentialite">Confidentialité</Link>
          <span className="text-[#D8D2C8]">·</span>
          <Link to="/politique-remboursement" className="hover:text-[#E85D04]" data-testid="footer-legal-remboursement">Politique de remboursement</Link>
        </p>
      </footer>
    </div>
  );
}
